import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { createSupplier } from "@/lib/masterData";

type SupplierCreateDraft = {
  name: string;
  currency: string;
  payment_terms: string;
  payment_method: string;
  mv_supplier_phone: string;
  mv_supplier_email: string;
  bill_to_address_line_1: string;
  bill_to_city: string;
  bill_to_state: string;
  bill_to_zip: string;
  sourcing_channel: "catalog_web" | "contract_manufacturer";
  hub_supply_chain_notes: string;
};

const EMPTY_DRAFT: SupplierCreateDraft = {
  name: "",
  currency: "",
  payment_terms: "",
  payment_method: "",
  mv_supplier_phone: "",
  mv_supplier_email: "",
  bill_to_address_line_1: "",
  bill_to_city: "",
  bill_to_state: "",
  bill_to_zip: "",
  sourcing_channel: "catalog_web",
  hub_supply_chain_notes: "",
};

function supplierIdFromError(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.body == null || typeof error.body !== "object") {
    return null;
  }
  const supplierId = (error.body as { supplier_id?: unknown }).supplier_id;
  return typeof supplierId === "number" && supplierId > 0 ? supplierId : null;
}

export function SupplierNewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SupplierCreateDraft>(EMPTY_DRAFT);
  const mutation = useMutation({
    mutationFn: () =>
      createSupplier({ ...draft }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["mrp-suppliers"] });
      navigate(`/mrp/suppliers/${created.supplier_id}`);
    },
  });
  const createdSupplierId = supplierIdFromError(mutation.error);

  function setField<K extends keyof SupplierCreateDraft>(key: K, value: SupplierCreateDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Creates the supplier in Megaventory with these fields, and a Freshdesk company with the same name.">
        Add a supplier
      </PageTitleWithHelp>
      <Card>
        <CardHeader>
          <CardTitle>Supplier</CardTitle>
        </CardHeader>
        <CardBody>
          <form
            className="grid gap-3 md:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              mutation.mutate();
            }}
          >
            <div className="grid gap-1">
              <Label htmlFor="new-supplier-name">Name</Label>
              <Input
                id="new-supplier-name"
                value={draft.name}
                onChange={(event) => setField("name", event.target.value)}
                placeholder="Supplier name"
                autoFocus
                required
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="new-supplier-currency">Currency</Label>
              <Input
                id="new-supplier-currency"
                value={draft.currency}
                onChange={(event) => setField("currency", event.target.value)}
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="new-supplier-terms">Payment terms</Label>
              <Input
                id="new-supplier-terms"
                value={draft.payment_terms}
                onChange={(event) => setField("payment_terms", event.target.value)}
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="new-supplier-method">Payment method</Label>
              <Input
                id="new-supplier-method"
                value={draft.payment_method}
                onChange={(event) => setField("payment_method", event.target.value)}
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="new-supplier-phone">Phone</Label>
              <Input
                id="new-supplier-phone"
                value={draft.mv_supplier_phone}
                onChange={(event) => setField("mv_supplier_phone", event.target.value)}
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="new-supplier-email">Email</Label>
              <Input
                id="new-supplier-email"
                type="email"
                value={draft.mv_supplier_email}
                onChange={(event) => setField("mv_supplier_email", event.target.value)}
              />
            </div>
            <div className="grid gap-1 md:col-span-2">
              <Label htmlFor="new-supplier-address">Bill to address</Label>
              <Input
                id="new-supplier-address"
                value={draft.bill_to_address_line_1}
                onChange={(event) => setField("bill_to_address_line_1", event.target.value)}
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="new-supplier-city">City</Label>
              <Input
                id="new-supplier-city"
                value={draft.bill_to_city}
                onChange={(event) => setField("bill_to_city", event.target.value)}
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="new-supplier-state">State</Label>
              <Input
                id="new-supplier-state"
                value={draft.bill_to_state}
                onChange={(event) => setField("bill_to_state", event.target.value)}
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="new-supplier-zip">Zip</Label>
              <Input
                id="new-supplier-zip"
                value={draft.bill_to_zip}
                onChange={(event) => setField("bill_to_zip", event.target.value)}
              />
            </div>
            <div className="grid gap-1 md:col-span-2">
              <Label htmlFor="new-supplier-channel">Sourcing channel</Label>
              <select
                id="new-supplier-channel"
                className="h-10 w-full max-w-md rounded-md border border-slate-300 bg-white px-3 text-sm"
                value={draft.sourcing_channel}
                onChange={(event) =>
                  setField(
                    "sourcing_channel",
                    event.target.value === "contract_manufacturer" ? "contract_manufacturer" : "catalog_web",
                  )
                }
              >
                <option value="catalog_web">Catalog webstore</option>
                <option value="contract_manufacturer">Contract manufacturer</option>
              </select>
            </div>
            <div className="grid gap-1 md:col-span-2">
              <Label htmlFor="new-supplier-notes">Hub supply chain notes</Label>
              <TextArea
                id="new-supplier-notes"
                rows={6}
                value={draft.hub_supply_chain_notes}
                onChange={(event) => setField("hub_supply_chain_notes", event.target.value)}
              />
              <p className="text-xs text-slate-500">
                Stored only in the hub. The other fields are sent to Megaventory. A Freshdesk company is created with the supplier name.
              </p>
            </div>
            {mutation.error ? (
              <p className="text-sm text-red-600 md:col-span-2">
                {(mutation.error as Error).message}
                {createdSupplierId != null ? (
                  <>
                    {" "}
                    <Link className="text-blue-700 hover:underline" to={`/mrp/suppliers/${createdSupplierId}`}>
                      Open the supplier
                    </Link>
                  </>
                ) : null}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2 md:col-span-2">
              <Button type="submit" isLoading={mutation.isPending} disabled={draft.name.trim() === ""}>
                Add supplier
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate("/mrp/suppliers")}>
                Cancel
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
